// ============================================================
// errorRecovery — エージェントのエラー分類と復帰プラン（Track B, 2026-07-04）
//
// 従来 useAgent 内に散在していたエラー判定 regex と復帰処理を1モジュールに集約。
// 「エラー種別 → 履歴の扱い + ユーザーへの提示 + 提示アクション」を
// 宣言的なプランとして返し、useAgent はそれを実行するだけにする。
// （DnD の interactionMachine と同じ「復帰パスを構造で保証する」方針）
//
// テスト: __tests__/errorRecovery.test.ts
// ============================================================

export type AgentErrorKind =
  /** Gemini の turn 順序エラー（履歴の不整合） */
  | 'turn_order'
  /** サンプリング揺らぎ等の一過性エラー（リトライで解消しやすい） */
  | 'transient'
  /** その他（ネットワーク・API エラー等） */
  | 'other';

/** エラーメッセージからエラー種別を分類 */
export function classifyAgentError(msg: string): AgentErrorKind {
  const m = String(msg || '');
  if (
    /function response turn/i.test(m)
    || /turn comes immediately after a function/i.test(m)
    || /function call turn/i.test(m)
  ) {
    return 'turn_order';
  }
  if (
    // Gemini が `print(default_api.X)` のような擬似コードを吐く既知バグ
    /MALFORMED_FUNCTION_CALL|Malformed function call/i.test(m)
    // finishReason: STOP かつ parts 空で返る既知バグ（PHP 中継が 502 で返す）
    || /Empty Gemini response/i.test(m)
    // 一時的な過負荷・レート制限
    || /\b(429|503)\b|overloaded|rate limit/i.test(m)
  ) {
    return 'transient';
  }
  return 'other';
}

/** エラーメッセージ下に提示する復帰アクション */
export type AgentErrorActionId = 'retry' | 'reset' | 'copy';

export interface ErrorRecoveryPlan {
  kind: AgentErrorKind;
  /** ユーザーに表示するメッセージ */
  userText: string;
  /** 会話履歴（contents）の扱い */
  history: 'keep' | 'prune' | 'clear';
  /** 提示する復帰アクション */
  actions: AgentErrorActionId[];
}

/**
 * エラー種別と発生状況から復帰プランを決める。
 * @param consecutiveTurnOrderErrors 連続した turn_order エラー回数（今回を含まない）
 */
export function planRecovery(
  msg: string,
  opts: { consecutiveTurnOrderErrors?: number } = {},
): ErrorRecoveryPlan {
  const kind = classifyAgentError(msg);
  const streak = opts.consecutiveTurnOrderErrors ?? 0;

  if (kind === 'turn_order') {
    if (streak >= 1) {
      // 切除で直らなかった → 全リセットが必要
      return {
        kind,
        userText: '会話履歴の修復に失敗したためリセットしました。もう一度メッセージを送ってください。',
        history: 'clear',
        actions: ['copy'],
      };
    }
    // 従来は即・全消しだったが、まず壊れたターンだけ切除して履歴を残す
    return {
      kind,
      userText: '会話履歴に不整合があったため、壊れた部分だけ取り除きました。「再試行」でそのまま続けられます。',
      history: 'prune',
      actions: ['retry', 'reset'],
    };
  }

  if (kind === 'transient') {
    return {
      kind,
      userText: 'AIの応答が不安定でした（自動再試行も失敗）。少し待ってから再試行してください。',
      history: 'keep',
      actions: ['retry', 'reset'],
    };
  }

  return {
    kind,
    userText: `通信エラー: ${msg}`,
    history: 'prune', // 防御的に孤立ターンを掃除（localStorage に破損状態を残さない）
    actions: ['retry', 'reset', 'copy'],
  };
}

/** アクションボタンの表示ラベル */
export const ERROR_ACTION_LABELS: Record<AgentErrorActionId, string> = {
  retry: '🔄 再試行',
  reset: '🗑 会話をリセット',
  copy: '📋 内容をコピー',
};

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * 一過性エラーのバックオフ付きリトライ（B4）。
 * transient 判定のエラーのみ再試行し、それ以外は即座に投げる。
 * @param retries 追加試行回数（既定2 = 最大3回実行）
 */
export async function withTransientRetry<T>(
  fn: () => Promise<T>,
  opts: { retries?: number; baseDelayMs?: number; onRetry?: (attempt: number, msg: string) => void } = {},
): Promise<T> {
  const retries = opts.retries ?? 2;
  const base = opts.baseDelayMs ?? 500;
  let lastErr: any;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fn();
    } catch (e: any) {
      lastErr = e;
      const msg = e?.message || String(e);
      if (classifyAgentError(msg) !== 'transient' || attempt === retries) throw e;
      opts.onRetry?.(attempt + 1, msg);
      // 指数バックオフ + ジッタ（500ms, 1000ms, ...）
      await sleep(base * Math.pow(2, attempt) + Math.floor(Math.random() * 250));
    }
  }
  throw lastErr;
}
